import React, { useCallback, useRef, useState } from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import useProvisionedSubscriptionMutations from "../../hooks/useProvisionedSubscriptionMutations";
import { serverErrorMessage } from "../../functions/provisionedSubscription";
import RenewProvisionedSubscriptionModal from "./RenewProvisionedSubscriptionModal";
import CancelProvisionedSubscriptionModal from "./CancelProvisionedSubscriptionModal";
import RefundProvisionedSubscriptionModal from "./RefundProvisionedSubscriptionModal";
import SetSiteModal from "./SetSiteModal";
import ProvisioningActionToast from "./ProvisioningActionToast";

const messages = defineMessages( {
	renewed: {
		id: "provisioning.toast.renewed",
		defaultMessage: "Subscription {number} extended with one billing term.",
	},
	cancelled: {
		id: "provisioning.toast.cancelled",
		defaultMessage: "Subscription {number} cancelled.",
	},
	refunded: {
		id: "provisioning.toast.refunded",
		defaultMessage: "Subscription {number} refunded.",
	},
	siteSet: {
		id: "provisioning.toast.siteSet",
		defaultMessage: "Site saved for subscription {number}.",
	},
} );

/**
 * One action that opens a modal for a subscription and submits a mutation on confirmation.
 *
 * `close` is a no-op while the mutation is in flight. The modal's Escape, backdrop and X all route
 * through it, and closing would reset the mutation and detach the per-call `onSuccess` that shows the
 * toast; the request would still complete, silently, and the user would retry it.
 *
 * @param {Object}   mutation       The TanStack mutation.
 * @param {Object}   successMessage The toast message, with a `number` value.
 * @param {Function} toVariables    Maps the target subscription and the modal's argument to the mutation variables.
 * @param {Function} showToast      Shows a toast for a subscription.
 *
 * @returns {{target: Object|null, open: Function, close: Function, confirm: Function}} The action.
 */
function useConfirmedAction( mutation, successMessage, toVariables, showToast ) {
	const [ target, setTarget ] = useState( null );

	const close = useCallback( () => {
		if ( mutation.isPending ) {
			return;
		}
		setTarget( null );
		mutation.reset();
	}, [ mutation ] );

	const confirm = useCallback( arg => {
		const subscription = target;
		mutation.mutate( toVariables( subscription, arg ), {
			onSuccess: () => {
				setTarget( null );
				showToast( "success", successMessage, subscription );
			},
		} );
	}, [ mutation, target, toVariables, successMessage, showToast ] );

	return { target, open: setTarget, close, confirm };
}

const renewVariables = subscription => ( { id: subscription.id } );
const cancelVariables = ( subscription, { immediately } ) => ( { id: subscription.id, data: { immediately } } );
const refundVariables = subscription => ( { id: subscription.id } );
const setSiteVariables = ( subscription, { site } ) => ( { id: subscription.id, data: { site } } );

/**
 * The row and detail actions on a provisioned subscription: renew, cancel, refund and set site.
 *
 * Owns which modal is open and for which subscription, plus the feedback toast, so the list page and
 * the detail page wire the actions once and render the same modals. Renew, cancel and refund confirm
 * first; set site asks for the URL and then submits.
 *
 * @returns {{openRenew: Function, openCancel: Function, openRefund: Function, openSetSite: Function,
 *            modals: JSX.Element}} The handlers and the element to render once.
 */
export default function useProvisionedSubscriptionActions() {
	const { renew, cancel, refund, setSite } = useProvisionedSubscriptionMutations();
	const [ toast, setToast ] = useState( null );
	const nextToastKey = useRef( 0 );

	const showToast = useCallback( ( variant, message, subscription ) => {
		nextToastKey.current += 1;
		setToast( {
			key: nextToastKey.current,
			variant,
			message: <FormattedMessage { ...message } values={ { number: subscription.subscriptionNumber } } />,
		} );
	}, [] );

	// The ui-library Toast calls `onDismiss` after its leave transition, so a toast that replaced the
	// dismissing one in the meantime must survive the late callback.
	const dismissToast = useCallback( key => {
		setToast( current => ( current && current.key === key ? null : current ) );
	}, [] );

	const renewAction   = useConfirmedAction( renew, messages.renewed, renewVariables, showToast );
	const cancelAction  = useConfirmedAction( cancel, messages.cancelled, cancelVariables, showToast );
	const refundAction  = useConfirmedAction( refund, messages.refunded, refundVariables, showToast );
	const setSiteAction = useConfirmedAction( setSite, messages.siteSet, setSiteVariables, showToast );

	const modals = (
		<>
			<RenewProvisionedSubscriptionModal
				subscription={ renewAction.target }
				isSubmitting={ renew.isPending }
				hasError={ renew.isError }
				errorMessage={ serverErrorMessage( renew.error ) }
				onCancel={ renewAction.close }
				onConfirm={ renewAction.confirm }
			/>
			<CancelProvisionedSubscriptionModal
				subscription={ cancelAction.target }
				isSubmitting={ cancel.isPending }
				hasError={ cancel.isError }
				errorMessage={ serverErrorMessage( cancel.error ) }
				onCancel={ cancelAction.close }
				onConfirm={ cancelAction.confirm }
			/>
			<RefundProvisionedSubscriptionModal
				subscription={ refundAction.target }
				isSubmitting={ refund.isPending }
				hasError={ refund.isError }
				errorMessage={ serverErrorMessage( refund.error ) }
				onCancel={ refundAction.close }
				onConfirm={ refundAction.confirm }
			/>
			<SetSiteModal
				subscription={ setSiteAction.target }
				isSubmitting={ setSite.isPending }
				hasError={ setSite.isError }
				errorMessage={ serverErrorMessage( setSite.error ) }
				onCancel={ setSiteAction.close }
				onSubmit={ setSiteAction.confirm }
			/>
			{ toast && (
				<ProvisioningActionToast
					key={ toast.key }
					variant={ toast.variant }
					onDismiss={ () => dismissToast( toast.key ) }
				>
					{ toast.message }
				</ProvisioningActionToast>
			) }
		</>
	);

	return {
		openRenew: renewAction.open,
		openCancel: cancelAction.open,
		openRefund: refundAction.open,
		openSetSite: setSiteAction.open,
		modals,
	};
}
